"use client";

import type { Media } from "@mw/shared/payload-types";
import type { Key } from "react-aria-components";

import Image from "next/image";
import { useTranslations } from "next-intl";
import { TabList as RACTabList, Tab as RACTab } from "react-aria-components";
import { useIsLargeOrAbove } from "@ui/hooks/useBreakpoint";
import { getImageProps } from "@utilities/payload";
import clsx from "clsx";
import { type RefObject, useEffect, useRef } from "react";
import * as m from "motion/react-m";

export interface OmniboxTabItem {
  description?: string | null;
  icon?: Media;
  title: string;
}

export interface OmniboxTabListProps {
  items: OmniboxTabItem[];
  mobileTabsBelow?: boolean;
  selectedKey: Key;
  centerRequestRef?: RefObject<number>;
}

export function OmniboxTabList({ items, selectedKey, mobileTabsBelow, centerRequestRef }: OmniboxTabListProps) {
  const t = useTranslations("ClientKeys.OmniboxTabList");
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
  const isLargeOrAbove = useIsLargeOrAbove();
  const lastHandledCenterRef = useRef(centerRequestRef?.current ?? 0);

  const selectedIdx = typeof selectedKey === "number" ? selectedKey : -1;

  // Scroll selected item into view (centered) — only after user-driven tab changes
  useEffect(() => {
    if (isLargeOrAbove || mobileTabsBelow || selectedIdx === -1) return;

    const currentRequest = centerRequestRef?.current ?? 0;
    if (currentRequest === lastHandledCenterRef.current) return;
    lastHandledCenterRef.current = currentRequest;

    const selectedItem = itemRefs.current[selectedIdx];
    if (!selectedItem) return;

    selectedItem.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "center",
    });
  }, [selectedIdx, isLargeOrAbove, mobileTabsBelow, centerRequestRef]);

  return (
    <div className='tw-relative tw-flow-root lg:tw-contents'>
      {/* Left fade gradient - only for horizontal scroll mode */}
      {!mobileTabsBelow && (
        <div
          className={clsx(
            "tw-absolute tw-top-0 tw-bottom-0 tw-w-16 tw-z-10 tw-pointer-events-none -tw-left-full-bleed lg:tw-hidden tw-transition-opacity tw-duration-300",
            selectedIdx === 0 && "tw-opacity-0",
          )}
          style={{
            background: "linear-gradient(to right, var(--marker-background-color, white), transparent)",
          }}
        />
      )}
      {/* Right fade gradient - only for horizontal scroll mode */}
      {!mobileTabsBelow && (
        <div
          className={clsx(
            "tw-absolute tw-top-0 tw-bottom-0 tw-w-16 tw-z-10 tw-pointer-events-none -tw-right-full-bleed lg:tw-hidden tw-transition-opacity tw-duration-300",
            selectedIdx === items.length - 1 && "tw-opacity-0",
          )}
          style={{
            background: "linear-gradient(to left, var(--marker-background-color, white), transparent)",
          }}
        />
      )}
      {/* Scroll container — vertical on mobile when mobileTabsBelow, horizontal otherwise */}
      <div
        className={clsx(
          "lg:tw-gap-6 lg:tw-mx-0 lg:tw-px-0 lg:tw-flex-col lg:tw-overflow-x-visible tw-flex",
          mobileTabsBelow
            ? "tw-flex-col tw-gap-6"
            : "tw-flex-row tw-gap-10 md:tw-gap-12 tw-overflow-x-auto no-scrollbar tw-scrollbar-none -tw-mx-full-bleed tw-px-full-bleed",
        )}
      >
        <RACTabList aria-label={t("feature_demos_aria_label")} className='tw-contents'>
          {items.map((item, idx) => {
            const iconProps = getImageProps(item.icon);

            return (
              <RACTab
                key={idx}
                id={idx}
                className='tw-shrink-0 tw-relative tw-outline-none focus-visible:tw-outline-focus focus-visible:tw-rounded tw-group tw-text-start tw-cursor-pointer'
              >
                {({ isSelected }) => (
                  <div
                    ref={el => {
                      itemRefs.current[idx] = el;
                    }}
                  >
                    <h3
                      className={clsx(
                        "tw-flex tw-flex-wrap tw-items-center tw-gap-3 tw-transition-transform tw-duration-150 tw-ease-out tw-type-base md:tw-type-lg lg:tw-type-2xl",
                        !isSelected &&
                          "tw-opacity-40 tw-transition-opacity tw-duration-150 tw-ease-out hover:tw-opacity-60",
                      )}
                    >
                      {iconProps && (
                        <span
                          className={clsx(
                            "tw-size-9 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-xl tw-border tw-border-black/10",
                            mobileTabsBelow ? "tw-inline-flex" : "tw-hidden lg:tw-inline-flex",
                          )}
                        >
                          <Image {...iconProps} className='tw-size-5' sizes='16px' />
                        </span>
                      )}
                      {item.title}
                    </h3>
                    {mobileTabsBelow && item.description && (
                      <m.div
                        animate={{ height: isSelected ? "auto" : 0 }}
                        className={clsx("tw-overflow-hidden", !mobileTabsBelow && "tw-hidden lg:tw-block")}
                        initial={false}
                        transition={{ duration: 0.3, type: "spring", stiffness: 320, damping: 32 }}
                      >
                        <m.p
                          animate={{ opacity: isSelected ? 0.6 : 0 }}
                          className='tw-pt-3 tw-type-base'
                          initial={false}
                          transition={{
                            duration: 0.2,
                            delay: isSelected ? 0.1 : 0,
                          }}
                        >
                          {item.description}
                        </m.p>
                      </m.div>
                    )}
                  </div>
                )}
              </RACTab>
            );
          })}
        </RACTabList>
      </div>
    </div>
  );
}
